import { useState } from "react";

// URL → embed URL (YouTube, Vimeo, Matterport/Kuula pass-through)
const toEmbedUrl = (url) => {
  if (!url) return url;
  try {
    const ytWatch = url.match(/youtube\.com\/watch\?v=([A-Za-z0-9_-]{6,})/);
    if (ytWatch) return `https://www.youtube.com/embed/${ytWatch[1]}`;
    const ytShort = url.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/);
    if (ytShort) return `https://www.youtube.com/embed/${ytShort[1]}`;
    const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    return url;
  } catch (e) {
    return url;
  }
};

const getYouTubeThumbnail = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{6,})/);
  return m ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : null;
};

export default function ImageGallery({
  imageUrls = [],
  floorPlanUrl = null,
  videoUrls = [],
  tourUrls = [],
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Build combined items array: floor plan → videos → tours → images
  const items = [];
  if (floorPlanUrl) {
    items.push({ url: floorPlanUrl, label: "Floor Plan", type: "floor_plan" });
  }
  (videoUrls || []).forEach((url, idx) => {
    items.push({
      url: toEmbedUrl(url),
      originalUrl: url,
      label: `Video${videoUrls.length > 1 ? " " + (idx + 1) : ""}`,
      type: "video",
    });
  });
  (tourUrls || []).forEach((url, idx) => {
    items.push({
      url: toEmbedUrl(url),
      originalUrl: url,
      label: `3D Tour${tourUrls.length > 1 ? " " + (idx + 1) : ""}`,
      type: "tour",
    });
  });
  (imageUrls || []).forEach((url, idx) => {
    items.push({ url, label: `Image ${idx + 1}`, type: "image" });
  });

  if (items.length === 0) return null;

  const openModal = (index) => {
    setCurrentIndex(index);
    setModalOpen(true);
  };

  const navigate = (dir) => {
    setCurrentIndex((prev) => {
      const next = prev + dir;
      if (next < 0) return items.length - 1;
      if (next >= items.length) return 0;
      return next;
    });
  };

  const renderTile = (item, index) => {
    const common = {
      minWidth: "250px",
      height: "200px",
      position: "relative",
      borderRadius: "8px",
      overflow: "hidden",
      cursor: "pointer",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    };

    if (item.type === "floor_plan") {
      return (
        <div key={index} style={{ ...common, border: "2px solid #4CAF50" }} onClick={() => openModal(index)}>
          <div style={{
            width: "100%", height: "100%",
            backgroundImage: `url(${item.url})`,
            backgroundSize: "cover", backgroundPosition: "center",
          }} />
          <div style={{
            position: "absolute", top: "8px", left: "8px",
            backgroundColor: "#4CAF50", color: "white",
            padding: "4px 8px", borderRadius: "4px",
            fontSize: "12px", fontWeight: "600",
          }}>Floor Plan</div>
        </div>
      );
    }

    if (item.type === "video" || item.type === "tour") {
      const isTour = item.type === "tour";
      const ytThumb = !isTour ? getYouTubeThumbnail(item.originalUrl) : null;
      const bg = ytThumb
        ? `url(${ytThumb}) center/cover`
        : isTour
          ? "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)"
          : "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)";
      const badgeColor = isTour ? "#6a11cb" : "#e53935";
      return (
        <div key={index} style={{ ...common, background: bg, border: `2px solid ${badgeColor}` }} onClick={() => openModal(index)}>
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            backgroundColor: ytThumb ? "rgba(0,0,0,0.3)" : "transparent",
          }}>
            <div style={{
              width: "60px", height: "60px", borderRadius: "50%",
              backgroundColor: "rgba(255,255,255,0.95)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "24px", color: "#000",
              boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
            }}>▶</div>
          </div>
          <div style={{
            position: "absolute", top: "8px", left: "8px",
            backgroundColor: badgeColor, color: "white",
            padding: "4px 8px", borderRadius: "4px",
            fontSize: "12px", fontWeight: "600",
          }}>{item.label}</div>
        </div>
      );
    }

    return (
      <div
        key={index}
        style={{
          ...common,
          backgroundImage: `url(${item.url})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        onClick={() => openModal(index)}
      />
    );
  };

  const current = items[currentIndex];
  const isMediaPlayer = current?.type === "video" || current?.type === "tour";

  return (
    <>
      {/* Scrollable Strip */}
      <div style={{ marginBottom: "24px", position: "relative" }}>
        <div style={{
          display: "flex",
          gap: "12px",
          overflowX: "auto",
          overflowY: "hidden",
          paddingBottom: "12px",
          scrollBehavior: "smooth",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "thin",
          scrollbarColor: "#888 #f1f1f1",
        }}>
          {items.map((item, index) => renderTile(item, index))}
        </div>
      </div>

      {/* Full-size Modal */}
      {modalOpen && current && (
        <div
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3000,
          }}
          onClick={() => setModalOpen(false)}
        >
          {/* Close button */}
          <div
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              color: "#fff",
              fontSize: "28px",
              cursor: "pointer",
              zIndex: 3001,
            }}
            onClick={() => setModalOpen(false)}
          >x</div>

          {/* Previous */}
          {items.length > 1 && (
            <div
              style={{
                position: "absolute",
                left: "20px",
                color: "#fff",
                fontSize: "36px",
                cursor: "pointer",
                padding: "10px",
                zIndex: 3001,
              }}
              onClick={(e) => { e.stopPropagation(); navigate(-1); }}
            >&#8249;</div>
          )}

          {/* Media */}
          {isMediaPlayer ? (
            <div
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}
              onClick={(e) => e.stopPropagation()}
            >
              <iframe
                title={current.label || "Media"}
                src={current.url}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen; xr-spatial-tracking"
                allowFullScreen
                style={{
                  width: "min(90vw, 1200px)",
                  height: "min(70vh, 675px)",
                  aspectRatio: "16 / 9",
                  border: "none",
                  borderRadius: "8px",
                  backgroundColor: "#000",
                }}
              />
              <a
                href={current.originalUrl || current.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#fff",
                  textDecoration: "none",
                  fontSize: "13px",
                  padding: "6px 14px",
                  backgroundColor: "rgba(255,255,255,0.18)",
                  borderRadius: "4px",
                }}
              >
                {current.type === "video" ? "Watch on YouTube ↗" : "Open tour in new tab ↗"}
              </a>
            </div>
          ) : (
            <img
              src={current.url}
              alt={current.label}
              style={{
                maxWidth: "90%",
                maxHeight: "90vh",
                objectFit: "contain",
                borderRadius: "4px",
              }}
              onClick={(e) => e.stopPropagation()}
            />
          )}

          {/* Next */}
          {items.length > 1 && (
            <div
              style={{
                position: "absolute",
                right: "20px",
                color: "#fff",
                fontSize: "36px",
                cursor: "pointer",
                padding: "10px",
                zIndex: 3001,
              }}
              onClick={(e) => { e.stopPropagation(); navigate(1); }}
            >&#8250;</div>
          )}

          {/* Counter */}
          <div style={{
            position: "absolute",
            bottom: "20px",
            color: "#fff",
            fontSize: "14px",
          }}>
            {currentIndex + 1} / {items.length} {current.label ? `- ${current.label}` : ""}
          </div>
        </div>
      )}
    </>
  );
}
